import React, { useState } from 'react';
import { TrendingUp, Flame, Star, Coffee, Grape, Droplets, Package, Sparkles, Heart, Zap, Shield, Leaf, Target, Brain, User, Palette, ShoppingCart, Plus } from 'lucide-react';
import QuizResultActions from './QuizResultActions';
import SaucePersonalizer from './SaucePersonalizer';
import { functionalIngredientUtils } from '../data/functionalIngredients';
import { sauceUtils } from '../data/sauces';

interface DashboardProps {
  sauceProfile: any;
  onRetakeQuiz?: () => void;
  onGoToShop?: () => void;
}

export default function Dashboard({ sauceProfile, onRetakeQuiz, onGoToShop }: DashboardProps) {
  const [personalizerOpen, setPersonalizerOpen] = useState(false);
  const [selectedSauce, setSelectedSauce] = useState<any>(null);
  
  const isPersonaQuiz = sauceProfile.quizType === 'persona';
  const isFlavorQuiz = sauceProfile.quizType === 'flavor';

  // Get recommended functional ingredients based on quiz results
  const recommendedIngredients = functionalIngredientUtils.getRecommendedIngredients(sauceProfile);

  const openPersonalizer = (sauce: any) => {
    setSelectedSauce(sauce);
    setPersonalizerOpen(true);
  };

  const handleAddToCart = (sauceId: string, selectedIngredients: string[], totalPrice: number) => {
    console.log('Added personalized sauce to cart:', { sauceId, selectedIngredients, totalPrice });
    // Here you would typically update a global cart state
  };

  const handleGoToShop = () => {
    if (onGoToShop) {
      onGoToShop();
    } else {
      console.warn('onGoToShop function not provided to Dashboard component');
    }
  };

  // Enhanced sauce recommendations display
  const renderSauceRecommendations = () => {
    if (!sauceProfile.sauceRecommendations) return null;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {sauceProfile.sauceRecommendations.slice(0, 6).map((sauce: any, index: number) => (
          <div key={sauce.id} className="bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-800 hover:border-cyan-500/30 transition-all duration-300 cursor-pointer group">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${sauce.color} rounded-xl flex items-center justify-center`}>
                  <Package className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold group-hover:text-cyan-400 transition-colors mb-1 text-sm sm:text-base">
                    {sauce.name}
                  </h4>
                  <p className="text-gray-400 text-xs sm:text-sm mb-2">{sauce.description}</p>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      sauce.heat === 'Extreme' ? 'bg-red-500/20 text-red-400' :
                      sauce.heat === 'Hot' ? 'bg-orange-500/20 text-orange-400' :
                      sauce.heat === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {sauce.heat}
                    </span>
                    <span className="text-gray-500 text-xs">#{index + 1} Match</span>
                  </div>
                  {sauce.reasons && sauce.reasons.length > 0 && (
                    <div className="space-y-1">
                      {sauce.reasons.map((reason: string, reasonIndex: number) => (
                        <p key={reasonIndex} className="text-cyan-300 text-xs">
                          • {reason}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                  <span className="text-cyan-400 text-sm sm:text-base font-semibold">
                    {sauce.compatibility}%
                  </span>
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 fill-current" />
                </div>
                <div className="w-12 sm:w-16 bg-gray-800 rounded-full h-1">
                  <div 
                    className={`bg-gradient-to-r ${sauce.color} h-1 rounded-full`}
                    style={{ width: `${sauce.compatibility}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            {/* NEW: Personalization CTA */}
            <div className="flex space-x-2 mt-3">
              <button
                onClick={() => {
                  const fullSauce = sauceUtils.getSauceById(sauce.id);
                  if (fullSauce) openPersonalizer(fullSauce);
                }}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-600 text-white py-2 px-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center space-x-2 text-xs"
              >
                <Sparkles className="h-3 w-3" />
                <span>Personalize</span>
              </button>
              <button className="bg-gradient-to-r from-pink-500 to-purple-600 text-white py-2 px-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center justify-center text-xs">
                <ShoppingCart className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    if (score >= 25) return 'text-orange-400';
    return 'text-red-400';
  };

  // Render different metrics based on quiz type
  const renderCoreMetrics = () => {
    if (isPersonaQuiz) {
      return [
        { label: 'Beauty Score', value: sauceProfile.beautyScore, icon: Heart, benefit: 'Skin & Anti-aging' },
        { label: 'Wellness Score', value: sauceProfile.wellnessScore, icon: Shield, benefit: 'Stress & Balance' },
        { label: 'Performance Score', value: sauceProfile.performanceScore, icon: Zap, benefit: 'Energy & Focus' },
        { label: 'Balance', value: sauceProfile.balance || 75, icon: Target, benefit: 'Harmony' }
      ];
    } else {
      return [
        { label: 'Intensity', value: sauceProfile.intensity, icon: TrendingUp, benefit: 'Flavor Impact' },
        { label: 'Heat Level', value: sauceProfile.heat, icon: Flame, benefit: 'Spice Tolerance' },
        { label: 'Complexity', value: sauceProfile.complexity, icon: Star, benefit: 'Flavor Layers' },
        { label: 'Adventure', value: sauceProfile.adventure, icon: Target, benefit: 'Exploration' }
      ];
    }
  };

  return (
    <div className="min-h-screen py-16 sm:py-20 relative overflow-hidden">
      {/* Enhanced Background with AI Particles */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-blue-600/20 animate-pulse"></div>
      
      {/* AI Neural Network Animation */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Enhanced Header with Quiz Type Indicator */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border border-cyan-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6">
            {isPersonaQuiz ? (
              <>
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
                <span className="text-cyan-300 font-medium text-sm sm:text-base">Sauce Persona Analysis</span>
              </>
            ) : (
              <>
                <Palette className="h-4 w-4 sm:h-5 sm:w-5 text-pink-400" />
                <span className="text-pink-300 font-medium text-sm sm:text-base">Flavor Profile Analysis</span>
              </>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3 sm:mb-4">
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Your Enhanced DrizzID™
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300">
            {isPersonaQuiz 
              ? 'Wellness-focused recommendations with personalization options'
              : 'Flavor-focused recommendations with personalization options'
            }
          </p>
        </div>

        {/* NEW: Functional Ingredients Recommendation Section */}
        {recommendedIngredients.length > 0 && (
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12">
            <div className="flex items-center space-x-3 sm:space-x-4 mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">Recommended Functional Ingredients</h2>
                <p className="text-gray-400 text-sm sm:text-base">
                  Based on your {isPersonaQuiz ? 'wellness goals' : 'flavor preferences'}, add these to any sauce
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {recommendedIngredients.map((ingredient) => (
                <div key={ingredient.id} className="bg-black/50 rounded-lg p-4 border border-gray-800 hover:border-cyan-500/30 transition-all duration-300">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-2xl">{ingredient.icon}</span>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{ingredient.name}</h4>
                      <p className="text-gray-400 text-xs">{ingredient.category}</p>
                    </div>
                    <div className="ml-auto text-cyan-400 font-bold text-sm">
                      +S${ingredient.price.toFixed(2)}
                    </div>
                  </div>
                  <p className="text-gray-300 text-xs mb-3">{ingredient.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {ingredient.benefits.slice(0, 2).map((benefit, index) => (
                      <span key={index} className="text-xs bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded-full">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm mb-4">
                Add these ingredients to any sauce when personalizing your order
              </p>
            </div>
          </div>
        )}

        {/* Enhanced Profile Analysis */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Enhanced Profile Type */}
            <div className="text-center lg:text-left">
              <div className={`inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r ${
                isPersonaQuiz ? 'from-cyan-500/20 to-blue-600/20 border-cyan-500/30' : 'from-pink-500/20 to-purple-600/20 border-pink-500/30'
              } border rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6`}>
                <Star className={`h-5 w-5 sm:h-6 sm:w-6 ${isPersonaQuiz ? 'text-cyan-400' : 'text-pink-400'}`} />
                <span className="text-lg sm:text-xl font-bold text-white">{sauceProfile.profileType}</span>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Your Enhanced DrizzID™</h2>
              
              {/* Display different personality info based on quiz type */}
              {isPersonaQuiz ? (
                <>
                  {sauceProfile.personaType && (
                    <p className="text-gray-400 mb-3 sm:mb-4 text-sm sm:text-base">
                      Persona Type: <span className="text-cyan-400 font-semibold">{sauceProfile.personaType}</span>
                    </p>
                  )}
                  
                  {sauceProfile.primaryGoals && sauceProfile.primaryGoals.length > 0 && (
                    <div className="bg-black/50 rounded-xl p-3 sm:p-4 mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-cyan-400 mb-2 sm:mb-3">Primary Goals</h3>
                      <div className="flex flex-wrap gap-2">
                        {sauceProfile.primaryGoals.map((goal: string, index: number) => (
                          <span key={index} className="bg-cyan-500/20 text-cyan-300 text-xs px-2 py-1 rounded-full">
                            {goal}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {sauceProfile.wellnessPhilosophy && (
                    <div className="bg-black/50 rounded-xl p-3 sm:p-4 mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-green-400 mb-2">Wellness Philosophy</h3>
                      <p className="text-gray-300 text-sm">{sauceProfile.wellnessPhilosophy}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {sauceProfile.flavorPersonality && (
                    <p className="text-gray-400 mb-3 sm:mb-4 text-sm sm:text-base">
                      Flavor Personality: <span className="text-pink-400 font-semibold">{sauceProfile.flavorPersonality}</span>
                    </p>
                  )}
                  
                  {sauceProfile.primaryCharacteristics && sauceProfile.primaryCharacteristics.length > 0 && (
                    <div className="bg-black/50 rounded-xl p-3 sm:p-4 mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-pink-400 mb-2 sm:mb-3">Primary Characteristics</h3>
                      <div className="flex flex-wrap gap-2">
                        {sauceProfile.primaryCharacteristics.map((char: string, index: number) => (
                          <span key={index} className="bg-pink-500/20 text-pink-300 text-xs px-2 py-1 rounded-full">
                            {char}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {sauceProfile.pairingStyle && (
                    <div className="bg-black/50 rounded-xl p-3 sm:p-4 mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-purple-400 mb-2">Pairing Style</h3>
                      <p className="text-gray-300 text-sm">{sauceProfile.pairingStyle}</p>
                    </div>
                  )}
                </>
              )}

              {sauceProfile.topSauceMatch && (
                <div className="bg-black/50 rounded-xl p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg font-semibold text-yellow-400 mb-2 sm:mb-3">Top Sauce Match</h3>
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r ${sauceProfile.topSauceMatch.color} rounded-lg`}></div>
                    <div>
                      <p className="text-white font-semibold text-sm sm:text-base">{sauceProfile.topSauceMatch.name}</p>
                      <p className="text-gray-400 text-xs sm:text-sm">{sauceProfile.topSauceMatch.compatibility}% compatibility</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Scores with Dynamic Metrics */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
              {renderCoreMetrics().map((metric, index) => (
                <div key={index} className="bg-black/50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <metric.icon className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
                    <span className={`font-bold text-sm sm:text-base ${getScoreColor(metric.value)}`}>
                      {metric.value}%
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs sm:text-sm">{metric.label}</p>
                  <p className="text-cyan-300 text-xs">{metric.benefit}</p>
                  <div className="w-full bg-gray-800 rounded-full h-1.5 sm:h-2 mt-2">
                    <div 
                      className="bg-gradient-to-r from-cyan-500 to-purple-600 h-1.5 sm:h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${metric.value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quiz Result Actions - Download & Email */}
        {sauceProfile.savedResult && (
          <div className="mb-8 sm:mb-12">
            <QuizResultActions 
              quizResult={sauceProfile.savedResult}
              profileData={sauceProfile}
            />
          </div>
        )}

        {/* Enhanced Sauce Recommendations Section */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12">
          <div className="flex items-center space-x-3 sm:space-x-4 mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Your Personalized Sauce Recommendations</h2>
              <p className="text-gray-400 text-sm sm:text-base">
                Based on your {isPersonaQuiz ? 'wellness goals and lifestyle needs' : 'unique flavor preferences'}
              </p>
            </div>
          </div>

          {renderSauceRecommendations()}

          <div className="mt-6 sm:mt-8 text-center">
            <button 
              onClick={handleGoToShop}
              className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2 mx-auto text-sm sm:text-base"
            >
              <Package className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Shop Your Matches</span>
            </button>
          </div>
        </div>

        {/* Enhanced Profile Insights - Different for Each Quiz Type */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center space-x-2 sm:space-x-3">
            <div className="bg-gradient-to-r from-cyan-500 to-purple-600 p-1.5 sm:p-2 rounded-lg">
              <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span>AI DrizzID™ Insights</span>
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {isPersonaQuiz ? (
              <>
                {/* Persona-specific insights */}
                <div className="text-center bg-black/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-pink-400 mb-2">Beauty Focus</h4>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    Your profile suggests optimal ingredients for skin health, anti-aging, and natural beauty enhancement
                  </p>
                  <div className="mt-2 sm:mt-3 bg-pink-500/20 rounded-full px-2 sm:px-3 py-1">
                    <span className="text-pink-300 text-xs font-semibold">{sauceProfile.beautyScore}% Compatibility</span>
                  </div>
                </div>
                
                <div className="text-center bg-black/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-green-400 mb-2">Wellness Support</h4>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    Adaptogenic compounds and stress-management ingredients align with your wellness priorities
                  </p>
                  <div className="mt-2 sm:mt-3 bg-green-500/20 rounded-full px-2 sm:px-3 py-1">
                    <span className="text-green-300 text-xs font-semibold">{sauceProfile.wellnessScore}% Compatibility</span>
                  </div>
                </div>
                
                <div className="text-center bg-black/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-blue-400 mb-2">Performance Boost</h4>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    Energy-enhancing and cognitive-supporting compounds match your performance goals
                  </p>
                  <div className="mt-2 sm:mt-3 bg-blue-500/20 rounded-full px-2 sm:px-3 py-1">
                    <span className="text-blue-300 text-xs font-semibold">{sauceProfile.performanceScore}% Compatibility</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Flavor-specific insights */}
                {sauceProfile.preferences && (
                  <div className="text-center bg-black/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Star className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-purple-400 mb-2">Flavor Preferences</h4>
                    <div className="space-y-1 text-xs sm:text-sm text-gray-400">
                      <p>Heat: {sauceProfile.preferences.heatLevel}</p>
                      <p>Complexity: {sauceProfile.preferences.complexityLevel}</p>
                      <p>Adventure: {sauceProfile.preferences.adventureLevel}</p>
                    </div>
                  </div>
                )}
                
                {sauceProfile.pairingStyle && (
                  <div className="text-center bg-black/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Target className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-green-400 mb-2">Pairing Style</h4>
                    <p className="text-gray-400 text-xs sm:text-sm">{sauceProfile.pairingStyle}</p>
                  </div>
                )}
                
                {sauceProfile.strengths && sauceProfile.strengths.length > 0 && (
                  <div className="text-center bg-black/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-blue-400 mb-2">Top Strengths</h4>
                    <div className="space-y-1 text-xs sm:text-sm text-gray-400">
                      {sauceProfile.strengths.slice(0, 3).map((strength: any, index: number) => (
                        <p key={index}>{strength.name}: {strength.score}%</p>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* AI Algorithm Explanation */}
          <div className="bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-cyan-500/20">
            <h4 className="text-base sm:text-lg font-semibold text-cyan-400 mb-3 sm:mb-4 flex items-center space-x-2">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>How Our AI Matched You</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <h5 className="text-white font-medium mb-2 text-sm sm:text-base">
                  {isPersonaQuiz ? 'Wellness-Focused Mapping' : 'Advanced Taste Mapping'}
                </h5>
                <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">
                  {isPersonaQuiz 
                    ? 'Our AI analyzed your wellness goals, lifestyle preferences, and functional ingredient interests to match you with sauces that support your health journey.'
                    : 'Our enhanced AI analyzed your responses across 8 comprehensive categories to map your flavor preferences against our 6 signature sauces with precision scoring.'
                  }
                </p>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                  <span className="text-cyan-300 text-xs">
                    {isPersonaQuiz ? 'Goal-oriented analysis' : 'Multi-dimensional analysis'}
                  </span>
                </div>
              </div>
              <div>
                <h5 className="text-white font-medium mb-2 text-sm sm:text-base">
                  {isPersonaQuiz ? 'Functional Ingredient Pairing' : 'Personalized Sauce Matching'}
                </h5>
                <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">
                  {isPersonaQuiz
                    ? 'Each recommendation includes functional ingredients that align with your beauty, wellness, and performance goals while maintaining delicious flavor profiles.'
                    : 'Each sauce recommendation includes compatibility scoring and specific reasons why it matches your unique taste profile and preferences.'
                  }
                </p>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <span className="text-purple-300 text-xs">
                    {isPersonaQuiz ? 'Wellness-driven recommendations' : 'Intelligent recommendations'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sauce Personalizer Modal */}
      {selectedSauce && (
        <SaucePersonalizer
          sauce={selectedSauce}
          isOpen={personalizerOpen}
          onClose={() => {
            setPersonalizerOpen(false);
            setSelectedSauce(null);
          }}
          onAddToCart={handleAddToCart}
          recommendedIngredients={recommendedIngredients}
          quizProfile={sauceProfile}
        />
      )}
    </div>
  );
}